import { create } from 'zustand';

export type UploadItemStatus =
  | 'queued'
  | 'requesting-url'
  | 'uploading'
  | 'confirming'
  | 'completed'
  | 'failed'
  | 'collision';

export interface UploadItem {
  localId: string;
  file: File;
  fileName: string;
  siteId: number;
  folderId?: string;
  status: UploadItemStatus;
  progress: number;
  speed: number;
  eta: number;
  error?: string;
  suggestedName?: string;
  fileId?: string;
  versionId?: string;
  uploadUrl?: string;
}

interface UploadTrayState {
  items: UploadItem[];
  isExpanded: boolean;
  isMinimized: boolean;
}

interface UploadTrayActions {
  addItems: (
    items: Array<{
      file: File;
      fileName: string;
      siteId: number;
      folderId?: string;
    }>
  ) => void;
  updateItem: (localId: string, update: Partial<UploadItem>) => void;
  retryItem: (localId: string) => void;
  resolveCollision: (localId: string, action: 'keep-both' | 'skip') => void;
  removeItem: (localId: string) => void;
  clearCompleted: () => void;
  expand: () => void;
  minimize: () => void;
  dismiss: () => void;
}

export const useUploadTrayStore = create<UploadTrayState & UploadTrayActions>(
  (set, get) => ({
    items: [],
    isExpanded: false,
    isMinimized: false,

    addItems: newItems => {
      const uploadItems: UploadItem[] = newItems.map(item => ({
        localId: crypto.randomUUID(),
        file: item.file,
        fileName: item.fileName,
        siteId: item.siteId,
        folderId: item.folderId,
        status: 'queued',
        progress: 0,
        speed: 0,
        eta: 0,
      }));
      set(state => ({
        items: [...state.items, ...uploadItems],
        isExpanded: true,
        isMinimized: false,
      }));
    },

    updateItem: (localId, update) =>
      set(state => ({
        items: state.items.map(item =>
          item.localId === localId ? { ...item, ...update } : item
        ),
      })),

    retryItem: localId =>
      set(state => ({
        items: state.items.map(item =>
          item.localId === localId
            ? {
                ...item,
                status: 'queued' as const,
                error: undefined,
                progress: 0,
                speed: 0,
                eta: 0,
              }
            : item
        ),
      })),

    resolveCollision: (localId, action) =>
      set(state => {
        if (action === 'skip') {
          return {
            items: state.items.filter(item => item.localId !== localId),
          };
        }
        // keep-both: rename to suggested name and re-queue
        return {
          items: state.items.map(item =>
            item.localId === localId && item.suggestedName
              ? {
                  ...item,
                  fileName: item.suggestedName,
                  suggestedName: undefined,
                  status: 'queued' as const,
                  error: undefined,
                  progress: 0,
                  speed: 0,
                  eta: 0,
                }
              : item
          ),
        };
      }),

    removeItem: localId =>
      set(state => ({
        items: state.items.filter(item => item.localId !== localId),
      })),

    clearCompleted: () =>
      set(state => ({
        items: state.items.filter(item => item.status !== 'completed'),
      })),

    expand: () => set({ isExpanded: true, isMinimized: false }),

    minimize: () => set({ isMinimized: true, isExpanded: false }),

    dismiss: () => {
      const remaining = get().items.filter(
        item =>
          item.status !== 'completed' &&
          item.status !== 'failed' &&
          item.status !== 'collision'
      );
      set({
        items: remaining,
        isExpanded: remaining.length > 0,
        isMinimized: false,
      });
    },
  })
);
